import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { examService, SheetScope } from '../../../services/examService';
import { SubjectStatus } from '../../../types';
import { MarksSheetDetail, RecordOutcome } from '../../../types/examination';
import { PaperDraft, draftOf, draftProblem, entryOf, sameDraft } from '../marks/paperStatus';

/** Why a paper wasn't saved; `code` 'changed' means someone else saved it meanwhile. */
export type Refused = RecordOutcome['refused'][number];

export interface SheetTally {
  present: number;
  absent: number;
  notAppeared: number;
  pending: number;
  locked: number;
  /** Average of the marks entered, as a % of the maximum. */
  average: number | null;
}

/**
 * One marks sheet being entered: the students, what has been typed for
 * each, and saving it. Only papers that changed are sent on a save; a
 * submit sends every open paper with `lock`. The server's refusals (a
 * paper someone else just submitted, say) are kept per student so the
 * sheet can say why.
 */
export const useMarksSheet = (scope: SheetScope | null) => {
  const [sheet, setSheet] = useState<MarksSheetDetail | null>(null);
  const [drafts, setDrafts] = useState<PaperDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<Record<string, Refused>>({});
  const seq = useRef(0);

  /** Read the sheet. `keep` holds typed papers to put back over it (ones the server refused). */
  const load = useCallback(async (keep: Record<string, PaperDraft> = {}) => {
    if (!scope) return;
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const next = await examService.getSheet(scope);
      if (mine !== seq.current) return;
      setSheet(next);
      setDrafts(next.students.map(s => keep[s.studentId] ?? draftOf(s.paper)));
    } catch (e: any) {
      if (mine === seq.current) setError(e?.message || 'Could not load this sheet');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    setSheet(null);
    setDrafts([]);
    setRefused({});
    if (scope) load();
  }, [scope, load]);

  const original = useMemo(() => sheet?.students.map(s => draftOf(s.paper)) ?? [], [sheet]);
  const isLocked = useCallback((i: number) => !!sheet?.students[i]?.paper.isLocked, [sheet]);

  const setDraft = useCallback((i: number, next: PaperDraft) => {
    setDrafts(prev => prev.map((d, k) => (k === i ? next : d)));
    setRefused(prev => {
      const id = sheet?.students[i]?.studentId;
      if (!id || !(id in prev)) return prev;
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }, [sheet]);

  const changed = useMemo(
    () => drafts.flatMap((d, i) => (!isLocked(i) && original[i] && !sameDraft(d, original[i]) ? [i] : [])),
    [drafts, original, isLocked],
  );
  const maxMarks = sheet?.maxMarks ?? 0;
  const problems = useMemo(
    () => drafts.flatMap((d, i) => (!isLocked(i) && draftProblem(d, maxMarks) ? [i] : [])),
    [drafts, maxMarks, isLocked],
  );

  const tally = useMemo<SheetTally>(() => {
    const t: SheetTally = { present: 0, absent: 0, notAppeared: 0, pending: 0, locked: 0, average: null };
    let sum = 0;
    let scored = 0;
    drafts.forEach((d, i) => {
      if (isLocked(i)) t.locked += 1;
      if (d.status === SubjectStatus.PRESENT && d.marks !== '') {
        t.present += 1;
        // An invalid mark (above the maximum) doesn't skew the running average.
        if (!draftProblem(d, maxMarks)) { sum += Number(d.marks); scored += 1; }
      }
      else if (d.status === SubjectStatus.ABSENT) t.absent += 1;
      else if (d.status === SubjectStatus.NOT_APPEARED) t.notAppeared += 1;
      else t.pending += 1;
    });
    if (scored && maxMarks) t.average = (sum / scored / maxMarks) * 100;
    return t;
  }, [drafts, isLocked, maxMarks]);

  /** Everyone still without a mark becomes "not appeared". Returns how many. */
  const markRestNotAppeared = useCallback(() => {
    let n = 0;
    setDrafts(prev => prev.map((d, i) => {
      if (isLocked(i) || d.status !== SubjectStatus.PENDING) return d;
      n += 1;
      return { status: SubjectStatus.NOT_APPEARED, marks: '' };
    }));
    return n;
  }, [isLocked]);

  /** Save what changed, or with `lock` submit every open paper. */
  const save = useCallback(async (lock: boolean): Promise<RecordOutcome | null> => {
    if (!scope || !sheet) return null;
    const indices = lock ? drafts.flatMap((_, i) => (isLocked(i) ? [] : [i])) : changed;
    if (!indices.length) return { saved: 0, unchanged: 0, refused: [] };
    const outcome = await examService.recordMarks(scope.examId, indices.map(i => ({
      studentId: sheet.students[i].studentId,
      subjectId: scope.subjectId,
      ...entryOf(drafts[i]),
      previous: entryOf(original[i]),
    })), lock);
    const typed = new Map(indices.map(i => [sheet.students[i].studentId, drafts[i]]));
    await load(Object.fromEntries(outcome.refused.map(r => [r.studentId, typed.get(r.studentId)!])));
    setRefused(Object.fromEntries(outcome.refused.map(r => [r.studentId, r])));
    return outcome;
  }, [scope, sheet, drafts, original, changed, isLocked, load]);

  /** Reopen the sheet's submitted papers, with the reason kept in each paper's history. */
  const unlock = useCallback(async (reason: string) => {
    if (!scope) return;
    await examService.unlockMarks(scope.examId, [scope.subjectId], { classId: scope.classId, sectionInstanceId: scope.sectionInstanceId }, reason);
    await load();
  }, [scope, load]);

  return { sheet, drafts, loading, error, refused, changed, problems, tally, isLocked, setDraft, markRestNotAppeared, save, unlock, reload: () => load() };
};
